import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { detailOr } from '@/services/api';
import { publicCatalogApi } from '@/features/publicsite/catalog/api/publicCatalogApi';
import { usePublicPageTitle } from '@/features/publicsite/usePublicPageTitle';
import type { ServiceRecord } from '@/features/publicsite/types/services/ServiceRecord';
import { formatDateTime } from '@/features/common/formatDate';
import { serviceEvaluationPage } from '@/i18n/dictionaries/serviceEvaluationPage';
import { useTranslation } from '@/i18n/useTranslation';

// The beneficiary-facing counterpart of ProviderPage's staff-recorded evaluation (ToR §6.4,
// "evaluarea calității ... serviciilor"): a link a provider hands the beneficiary in person after
// a delivery, unauthenticated because the beneficiary on a service_record is not necessarily a
// platform account — the record's own id, unguessable, is what proves this is the right delivery
// to rate, the same trust model /api/v1/media/*/download already uses for a shared file link.
export function ServiceEvaluationPage() {
  const { t, language } = useTranslation(serviceEvaluationPage);
  const { recordId = '' } = useParams();
  const [record, setRecord] = useState<ServiceRecord | null>(null);
  const [missing, setMissing] = useState(false);
  const [rating, setRating] = useState(5);
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    publicCatalogApi
      .serviceRecord(recordId)
      .then((r) => {
        if (live) setRecord(r);
      })
      .catch(() => {
        if (live) setMissing(true);
      });
    return () => {
      live = false;
    };
  }, [recordId]);

  usePublicPageTitle(missing ? t('notFound') : (record?.serviceTitle ?? null));

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await publicCatalogApi.evaluateServiceRecord(recordId, {
        rating,
        comments: comments || null,
      });
      setSubmitted(true);
    } catch (e) {
      setError(detailOr(e, t('submitError')));
    } finally {
      setSubmitting(false);
    }
  };

  if (missing) {
    return (
      <section className="container section">
        <h1>{t('notFound')}</h1>
      </section>
    );
  }

  if (!record) {
    return (
      <section className="container section">
        <p className="empty-state">{t('loading')}</p>
      </section>
    );
  }

  if (submitted || record.alreadyEvaluated) {
    const shownRating = submitted ? rating : record.rating;
    const shownComments = submitted ? comments : record.comments;
    return (
      <section className="container section">
        <h1>{submitted ? t('thankYouTitle') : t('alreadyEvaluatedTitle')}</h1>
        <p>
          {submitted
            ? t('thankYouBody')
            : t('alreadyEvaluatedBody', {
                rating: shownRating ?? '',
                comments: shownComments ? ` — ${shownComments}` : '',
              })}
        </p>
      </section>
    );
  }

  return (
    <section className="container section">
      <h1>{t('heading')}</h1>
      <div className="card evaluation-card">
        <h2>{record.serviceTitle}</h2>
        {record.providerName && (
          <p className="page-sub">{t('byProvider', { name: record.providerName })}</p>
        )}
        <p className="page-sub">
          {t('deliveredOn', { date: formatDateTime(record.deliveredAt, language) })}
        </p>

        {error && <p className="field-error">{error}</p>}

        <label>
          {t('ratingLabel')}
          <br />
          <select value={rating} onChange={(e) => setRating(Number(e.target.value))}>
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <br />
        <br />
        <label>
          {t('commentsLabel')}
          <br />
          <textarea
            className="evaluation-card__input"
            rows={3}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
          />
        </label>
        <p>
          <button className="btn btn--primary" disabled={submitting} onClick={() => void submit()}>
            {submitting ? t('submittingButton') : t('submitButton')}
          </button>
        </p>
      </div>
    </section>
  );
}
